import type { Material } from '@babylonjs/core/Materials/material';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { BusConfig, TrafficKind } from '../../config/bus.config';
import type { TrackManager } from '../../level/TrackManager';
import { trackFrame } from '../../level/TrackManager';
import type { EntityVisual } from '../../rendering/EntityVisual';
import type { VisualPool } from '../../rendering/VisualPool';
import { lerp } from '../../utils/math';
import type { BusTraffic, TrafficCar } from './BusTraffic';

/** Wheel radius per model (m) – the wheels turn with the distance driven. */
const WHEEL_RADIUS: Readonly<Record<TrafficKind, number>> = { bulli: 0.3275 };
/** Yaw into a lane change: rad per (lateral / forward speed). */
const LANE_YAW = 0.9;

interface Wheel {
  readonly node: TransformNode;
  readonly rest: Quaternion | null;
}

interface Entry {
  readonly car: TrafficCar;
  readonly visual: EntityVisual;
  readonly wheels: readonly Wheel[];
  readonly blinkLeft: readonly AbstractMesh[];
  readonly blinkRight: readonly AbstractMesh[];
  /** Meshes whose paint was swapped – their own material goes back on release. */
  readonly painted: readonly { readonly mesh: AbstractMesh; readonly material: Material | null }[];
}

/**
 * Visual side of the ride's traffic (BusTraffic is the gameplay): one pooled model per car, placed along the track
 * between fixed steps (curves, crossings), turned towards where it drives (oncoming cars face the van), pointing
 * into its lane changes, blinking before one, wheels rolling; a rammed car spins. The craftsmen's vans get their
 * paint per car (MAT_*_PAINT, one shared material per colour). Presentation only.
 */
export class BusTrafficPresenter {
  private readonly entries = new Map<number, Entry>();
  private readonly frame = trackFrame();
  private readonly turn = new Quaternion();
  private readonly paints = new Map<string, Material>();
  private time = 0;

  constructor(
    private readonly pool: VisualPool,
    private readonly track: TrackManager,
    private readonly traffic: BusTraffic,
    private readonly config: BusConfig,
    /** Fixed simulation step (s): lateral speed between two steps. */
    private readonly fixedStep: number,
  ) {}

  render(alpha: number, dt: number, paused: boolean): void {
    if (!paused) this.time += dt;
    const live = new Set<number>();
    for (const car of this.traffic.cars) {
      live.add(car.id);
      let entry = this.entries.get(car.id);
      if (!entry) {
        entry = this.create(car);
        this.entries.set(car.id, entry);
      }
      this.update(entry, alpha);
    }
    for (const [id, entry] of this.entries) {
      if (live.has(id)) continue;
      this.release(entry);
      this.entries.delete(id);
    }
  }

  reset(): void {
    for (const entry of this.entries.values()) this.release(entry);
    this.entries.clear();
  }

  private update(entry: Entry, alpha: number): void {
    const { car } = entry;
    const prev = car.previous;
    const pose = car.pose;
    const distance = lerp(prev.distance, pose.distance, alpha);
    const x = lerp(prev.x, pose.x, alpha);
    const y = lerp(prev.y, pose.y, alpha);
    const spin = lerp(prev.spin, pose.spin, alpha);
    this.track.trackToWorld(x, y, distance, this.frame);
    const root = entry.visual.root;
    root.position.set(this.frame.x, this.frame.y, this.frame.z);
    root.rotationQuaternion = null;
    const lateral = car.wrecked ? 0 : (pose.x - prev.x) / this.fixedStep;
    const into = Math.atan2(lateral, Math.max(2, car.speed)) * LANE_YAW * car.dir;
    root.rotation.set(0, this.frame.yaw + (car.dir < 0 ? Math.PI : 0) + into + spin, 0);

    // wheels roll with the distance (forward for the car: an oncoming one drives towards smaller distances)
    const roll = (car.dir * distance) / WHEEL_RADIUS[car.kind];
    for (const wheel of entry.wheels) {
      Quaternion.RotationYawPitchRollToRef(0, roll, 0, this.turn);
      wheel.node.rotationQuaternion ??= new Quaternion();
      if (wheel.rest) wheel.rest.multiplyToRef(this.turn, wheel.node.rotationQuaternion);
      else wheel.node.rotationQuaternion.copyFrom(this.turn);
    }

    // blinker before (and during) a lane change
    const on = car.signal !== 0 && Math.sin(this.time * Math.PI * 2 * this.config.presentation.blinkHz) > 0;
    // an oncoming car faces the van: its own left is the van's right
    const left = car.signal * car.dir < 0;
    for (const mesh of entry.blinkLeft) mesh.isVisible = on && left;
    for (const mesh of entry.blinkRight) mesh.isVisible = on && !left;
  }

  private create(car: TrafficCar): Entry {
    const kind = this.config.traffic.kinds[car.kind];
    const visual = this.pool.acquire(kind.assetId, null);
    const wheels: Wheel[] = [];
    for (const node of visual.root.getChildTransformNodes(false)) {
      const name = node.name.slice(node.name.lastIndexOf(':') + 1);
      if (name.includes('_PIVOT_WHEEL_')) wheels.push({ node, rest: node.rotationQuaternion?.clone() ?? null });
    }
    const meshes = visual.root.getChildMeshes(false);
    const blinkLeft = meshes.filter((m) => m.name.endsWith('BLINK_L'));
    const blinkRight = meshes.filter((m) => m.name.endsWith('BLINK_R'));
    for (const mesh of [...blinkLeft, ...blinkRight]) mesh.isVisible = false;
    const painted: { mesh: AbstractMesh; material: Material | null }[] = [];
    const hex = car.paint >= 0 ? kind.paints[car.paint] : undefined;
    if (hex) {
      for (const mesh of meshes) {
        const material = mesh.material;
        if (!material || !material.name.includes('PAINT')) continue;
        painted.push({ mesh, material });
        mesh.material = this.paint(material, hex);
      }
    }
    return { car, visual, wheels, blinkLeft, blinkRight, painted };
  }

  /** One shared copy of the model's paint per colour. */
  private paint(base: Material, hex: string): Material {
    const key = `${base.name}|${hex}`;
    let material = this.paints.get(key);
    if (!material) {
      material = base.clone(`${base.name}_${hex.slice(1)}`) ?? base;
      const color = Color3.FromHexString(hex).toLinearSpace();
      const target = material as Material & { albedoColor?: Color3; diffuseColor?: Color3 };
      if (target.albedoColor) target.albedoColor = color;
      else if (target.diffuseColor) target.diffuseColor = color;
      this.paints.set(key, material);
    }
    return material;
  }

  private release(entry: Entry): void {
    for (const { mesh, material } of entry.painted) mesh.material = material;
    for (const mesh of [...entry.blinkLeft, ...entry.blinkRight]) mesh.isVisible = true;
    for (const wheel of entry.wheels) wheel.node.rotationQuaternion = wheel.rest?.clone() ?? null;
    entry.visual.root.rotation.set(0, 0, 0);
    this.pool.release(entry.visual);
  }
}
